import type { ReactNode } from 'react'

import { actorOf } from '@brickup/shared/locales/de/history'
import { formatDateTime } from '@brickup/shared/utils'
import { cn, listRow } from '@/components/base'

/**
 * One entry of a change list: when, by whom, and what changed.
 *
 * Every Internal surface that lists changes at full width draws this frame —
 * the catalogue history and a project's configurator history — so an operator
 * reads the same row on both. The stamp column is fixed-width and the body
 * takes the rest, which is what lines the timestamps up down a long list. A
 * narrow panel that lists changes in one line per entry reads the same
 * attribution through `actorOf`.
 */
export function ChangeEntryRow({
  at,
  actorName,
  actorEmail,
  aside,
  action,
  children,
}: {
  at: string
  actorName: string | null
  actorEmail: string | null
  /** Under the actor — what the entry is, where the name alone does not say
   *  it. A side, a kind. */
  aside?: ReactNode
  /** At the trailing edge: an act on this entry, such as undoing it. */
  action?: ReactNode
  /** What changed. The caller owns its own shape; the row owns the column. */
  children: ReactNode
}) {
  return (
    <div className={cn(listRow(), 'items-start gap-3 px-0 py-3')}>
      {/* `items-start` so anything in `aside` is the width of its own content:
          a column stretches its children, and a pill as wide as the stamp
          above it reads as a bar. */}
      <div className="flex w-44 shrink-0 flex-col items-start gap-1">
        <span className="text-xs tabular-nums text-muted-foreground">
          {formatDateTime(at)}
        </span>
        <span className="max-w-full truncate text-xs text-faint">
          {actorOf(actorName, actorEmail)}
        </span>
        {aside}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1.5">{children}</div>

      {action}
    </div>
  )
}
